import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import PublicFooter from "@/components/public/PublicFooter";
import LangCurrencySelector from "@/components/public/LangCurrencySelector";

/* ═════════════════════════════════════════════════════════════════════
   PublicShell — nav + main + PublicFooter. Wrapper for every public
   marketing page (Landing, Solutions, all legal / trust / help pages).

   • Sticky nav with product/how-it-works/pricing/security/resources
   • Auth-aware: signed-out → Sign In + Get Started (gold),
                 signed-in → Open Dashboard
   • Global CookieConsent lives at the App root, not here.
   ═════════════════════════════════════════════════════════════════════ */

const NAV_LINKS = [
    { key: "nav.product",   fallback: "Product",   href: "/product"  },
    { key: "nav.together",  fallback: "Together",  href: "/together" },
    { key: "nav.solutions", fallback: "Solutions", href: "/solutions/individuals" },
    { key: "nav.learn",     fallback: "Learn",     href: "/learn"    },
    { key: "nav.security",  fallback: "Security",  href: "/security" },
    { key: "nav.pricing",   fallback: "Pricing",   href: "/pricing"  },
    { key: "nav.demo",      fallback: "Demo",      href: "/demo"     },
];

export default function PublicShell({ children }) {
    const { user } = useAuth();
    const { t } = useLocale();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="ps-root">
            <style>{styles}</style>
            <header className={`ps-nav ${scrolled ? "ps-nav--scrolled" : ""}`}>
                <div className="ps-nav-inner">
                    <Link to="/" className="ps-brand" data-testid="public-brand">
                        <span className="ps-brand-word">VAULT</span>
                        <span className="ps-brand-tag">by PD</span>
                    </Link>
                    <nav className="ps-nav-links" aria-label="Main">
                        {NAV_LINKS.map((l) => (
                            <Link key={l.href} to={l.href} className="ps-nav-link">
                                {l.fallback}
                            </Link>
                        ))}
                    </nav>
                    <div className="ps-nav-ctas">
                        <LangCurrencySelector />
                        {user ? (
                            <Link to="/dashboard" className="ps-btn ps-btn--gold" data-testid="nav-open-dashboard">Open Dashboard</Link>
                        ) : (
                            <>
                                <Link to="/login" className="ps-nav-link ps-hide-sm" data-testid="nav-signin">{t("nav.sign_in")}</Link>
                                <Link to="/signup" className="ps-btn ps-btn--gold" data-testid="nav-get-started">{t("nav.start_free")}</Link>
                            </>
                        )}
                    </div>
                </div>
            </header>
            <main>{children}</main>
            <PublicFooter />
        </div>
    );
}

const styles = `
.ps-root { background: #0B1122; color: #F6F3EC; min-height: 100vh; font-family: "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; }
.ps-nav { position: sticky; top: 0; z-index: 40; background: rgba(11,17,34,0.82); backdrop-filter: blur(12px); border-bottom: 1px solid transparent; transition: border-color .2s; }
.ps-nav--scrolled { border-bottom-color: rgba(246,243,236,0.08); }
.ps-nav-inner { max-width: 1200px; margin: 0 auto; padding: 20px 28px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.ps-brand { display: flex; align-items: baseline; gap: 8px; }
.ps-brand-word { font-weight: 900; font-size: 22px; letter-spacing: 0.22em; color: #F6F3EC; }
.ps-brand-tag { font-weight: 600; font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; color: rgba(246,243,236,0.45); transform: translateY(-2px); }
.ps-nav-links { display: none; gap: 32px; }
@media (min-width: 900px) { .ps-nav-links { display: flex; } }
.ps-nav-link { font-size: 14px; color: rgba(246,243,236,0.72); font-weight: 500; }
.ps-nav-link:hover { color: #F6F3EC; }
.ps-nav-ctas { display: flex; align-items: center; gap: 14px; }
@media (max-width: 640px) { .ps-hide-sm { display: none; } }
.ps-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 999px; font-size: 14px; font-weight: 600; transition: transform .15s, background .15s; }
.ps-btn--gold { background: #C6A86B; color: #0B1122; }
.ps-btn--gold:hover { background: #d3b978; transform: translateY(-1px); }
`;
